/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 17:53:42
 *
 *******************************************************/
//@flow

import React, { useEffect, useRef, useState } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { Icon } from '@shopify/polaris';
import { ChevronLeftMinor, ChevronRightMinor } from '@shopify/polaris-icons';

const styles = {
  root: {},
  itemSnapPoint: {
    scrollSnapAlign: 'start'
  }
};

export const Carousel = ({
  items,
  hideAction = false,
  hasSlideControls = false,
  alwaysShowSlide = false,
  autoDisplay = false,
  autoHeight = false,
  renderItem,
  handleChange
}) => {
  const listRef = useRef(null);

  const [heights, setHeights] = useState([]);
  const { scrollRef, refresh, pages, activePageIndex, prev, next, goTo } = useSnapCarousel();

  const handleUpdateIndex = index => {
    if (handleChange) {
      handleChange(index);
    }
  };

  useEffect(() => {
    if (autoDisplay) {
      const autoNextByTime = setTimeout(() => {
        if (activePageIndex + 1 < pages.length) {
          goTo(activePageIndex + 1);
          handleUpdateIndex(activePageIndex + 1);
        } else {
          // items
          goTo(0);
          handleUpdateIndex(0);
        }
      }, 3000);
      return () => {
        clearTimeout(autoNextByTime);
      };
    }
  }, [activePageIndex, autoDisplay, pages.length]);

  useEffect(() => {
    refresh();
    const newHeights = [];
    setHeights(newHeights);

    if (listRef.current) {
      const children = listRef.current.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        newHeights[i] = child.offsetHeight;
      }
      setHeights(newHeights);
    }
  }, [items, refresh]);

  return (
    <div style={{ height: autoHeight ? heights[activePageIndex] : 'auto' }} className="BC-Carousel">
      <ul
        ref={node => {
          listRef.current = node;
          scrollRef(node);
        }}
        className="carousel-cards">
        {items.map((item, i) =>
          renderItem ? (
            renderItem({ item, index: i })
          ) : (
            <li
              key={`element-${i}`}
              className={`carousel-item has-${pages?.length}-pages has-${pages[0]?.length}-items-in-pages`}
              style={{
                ...styles.item
              }}>
              <div>{item}</div>
            </li>
          )
        )}
      </ul>
      {(items.length > 1 || alwaysShowSlide) && (
        <div className="carousel-controls" aria-hidden>
          {!hideAction && (
            <button
              className={`carousel-btn ${activePageIndex === 0 ? 'carousel-btn--disabled' : ''} `}
              onClick={() => prev()}>
              <Icon source={ChevronLeftMinor} color="subdued" />
            </button>
          )}
          {pages.map((_, i) => (
            <button
              key={i}
              className={`carousel-pagination ${
                activePageIndex === i ? 'carousel-pagination--active' : ''
              } `}
              onClick={() => goTo(i)}>
              <div />
            </button>
          ))}
          {!hideAction && (
            <button
              className={`carousel-btn ${
                activePageIndex === pages.length - 1 ? 'carousel-btn--disabled' : ''
              } `}
              onClick={() => next()}>
              <Icon source={ChevronRightMinor} color="subdued" />
            </button>
          )}
        </div>
      )}
      {hasSlideControls && (
        <div className="slide-controls">
          {activePageIndex > 0 && (
            <div className={`btn-prev`} onClick={() => prev()}>
              <Icon source={ChevronLeftMinor} color="subdued" />
            </div>
          )}
          {activePageIndex < pages.length - 1 && (
            <div className={`btn-next`} onClick={() => next()}>
              <Icon source={ChevronRightMinor} color="subdued" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CarouselItem = ({ children }) => {
  return children;
};
